import React, { useState, useEffect } from 'react';
import '../assets/css/pages/profile.css';
import { useAuth } from '../context/AuthContext';
import Button from '../components/elements/button';
import FormInput from '../components/elements/form_input';
import PhoneInput from '../components/elements/phone_input';
import apiConfig from '../config/api_config';
import { toast } from 'react-toastify';
// import defaultAvatar from '../assets/images/default-avatar.png';

const UserProfile = () => {
    const { user, updateUser } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');
    const [personalInfo, setPersonalInfo] = useState({
        firstName: user?.first_name || '',
        lastName: user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
    });

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [userStats, setUserStats] = useState({
        farms: 0,
        cases: 0,
        orders: 0
    });
    const [notifications, setNotifications] = useState({
        email: true,
        sms: false,
        newsletter: true
    });

    // Fetch user stats on mount
    useEffect(() => {
        fetchUserStats();
    }, []);

    // Update personal info when user changes
    useEffect(() => {
        if (user) {
            setPersonalInfo({
                firstName: user.first_name || '',
                lastName: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
            });
        }
    }, [user]);

    const fetchUserStats = async () => {
        try {
            // Fetch user's farms count
            const farmsResponse = await apiConfig.get('/farms/');
            const farmsCount = farmsResponse?.data?.data?.length || 0;

            // Fetch user's cases count
            const casesResponse = await apiConfig.get('/cases/');
            const casesCount = casesResponse?.data?.data?.length || 0;

            // Fetch user's orders count
            const ordersResponse = await apiConfig.get('/orders/');
            const ordersCount = ordersResponse?.data?.data?.length || 0;

            setUserStats({
                farms: farmsCount,
                cases: casesCount,
                orders: ordersCount
            });
        } catch (error) {
            console.error('Error fetching user stats:', error);
        }
    };

    const handlePersonalInfoSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const response = await apiConfig.put('/auth/profile/', {
                first_name: personalInfo.firstName,
                last_name: personalInfo.lastName,
                phone: personalInfo.phone
            });
            
            if (response?.data?.data && updateUser) {
                updateUser(response.data.data);
            }
            
            toast.success('Profile updated successfully');
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setIsSaving(false);
        }
    };

    // const handlePasswordChange = (e) => {
    //     e.preventDefault();
    //     // Handle password change
    // };

    const renderPersonalInfo = () => (
        <div className="profile-section">
            <div className="section-title">
                Personal Information
                <Button
                    text={isSaving ? "Saving..." : (isEditing ? "Save Changes" : "Edit")}
                    variant="primary"
                    onClick={isEditing ? handlePersonalInfoSubmit : () => setIsEditing(true)}
                    loading={isSaving}
                    disabled={isSaving}
                />
            </div>
            <form onSubmit={handlePersonalInfoSubmit}>
                <div className="form-grid">
                    <FormInput
                        label="First Name"
                        showLabel
                        value={personalInfo.firstName}
                        onChange={(e) => setPersonalInfo({...personalInfo, firstName: e.target.value})}
                        disabled={!isEditing}
                    />
                    <FormInput
                        label="Last Name"
                        showLabel
                        value={personalInfo.lastName}
                        onChange={(e) => setPersonalInfo({...personalInfo, lastName: e.target.value})}
                        disabled={!isEditing}
                    />
                    <FormInput
                        label="Email"
                        type="email"
                        value={personalInfo.email}
                        disabled
                    />
                    <PhoneInput
                        label="Phone"
                        showLabel
                        value={personalInfo.phone}
                        onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                        disabled={!isEditing}
                        defaultCountryCode="+234"
                    />
                </div>
            </form>
        </div>
    );

    const renderSecurity = () => (
        <div className="profile-section">
            <h3 className="section-title">Security Settings</h3>
            <div className="security-item">
                <div className="security-info">
                    <h4>Password</h4>
                    <p>Update your password regularly for security</p>
                </div>
                <Button
                    text="Change Password"
                    variant="secondary"
                    onClick={() => window.location.href = '/forgot-password'}
                />
            </div>
            <div className="security-item">
                <div className="security-info">
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                </div>
                <Button
                    text="Enable"
                    variant="secondary"
                    onClick={() => {/* Handle 2FA setup */}}
                />
            </div>
        </div>
    );

    const renderPreferences = () => (
        <div className="profile-section">
            <h3 className="section-title">Notification Preferences</h3>
            <div className="preference-toggle">
                <div>
                    <h4>Email Notifications</h4>
                    <p>Receive updates and alerts via email</p>
                </div>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={notifications.email}
                        onChange={(e) => setNotifications({...notifications, email: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                </label>
            </div>
            <div className="preference-toggle">
                <div>
                    <h4>SMS Notifications</h4>
                    <p>Receive updates and alerts via SMS</p>
                </div>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={notifications.sms}
                        onChange={(e) => setNotifications({...notifications, sms: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                </label>
            </div>
            <div className="preference-toggle">
                <div>
                    <h4>Newsletter</h4>
                    <p>Receive our newsletter with updates and tips</p>
                </div>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={notifications.newsletter}
                        onChange={(e) => setNotifications({...notifications, newsletter: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                </label>
            </div>
        </div>
    );

    return (
        <div className="profile-page">
            <div className="profile-header">
                <div className="profile-avatar">
                    <img src={user?.avatar || '/default-avatar.png'} alt="Profile" />
                    <div className="avatar-upload">
                        Change Photo
                    </div>
                </div>
                <div className="profile-info">
                    <h2 className="profile-name">{personalInfo.firstName} {personalInfo.lastName}</h2>
                    <p className="profile-email">{personalInfo.email}</p>
                    <div className="profile-stats">
                        <div className="stat-item">
                            <div className="stat-value">{userStats.farms}</div>
                            <div className="stat-label">Farms</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">{userStats.cases}</div>
                            <div className="stat-label">Cases</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-value">{userStats.orders}</div>
                            <div className="stat-label">Orders</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="profile-content">
                <div className="profile-tabs">
                    <button 
                        className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
                        onClick={() => setActiveTab('personal')}
                    >
                        Personal Info
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        Security
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
                        onClick={() => setActiveTab('preferences')}
                    >
                        Preferences
                    </button>
                </div>

                <div className="profile-sections">
                    {activeTab === 'personal' && renderPersonalInfo()}
                    {activeTab === 'security' && renderSecurity()}
                    {activeTab === 'preferences' && renderPreferences()}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
